import React, { useState, useEffect } from 'react';
import { CollaboratorAuthService } from '../../../lib/services/collaboratorAuthService';
import { useProjectStore } from '../../../lib/stores/projectStore';
import { MinimalLoader } from '../../ui/MinimalLoader';
import { Plus, X } from 'lucide-react';
import { generatePassword } from './generatePassword';

// Spostamento puro da CollaboratorPortalAccessManager.tsx (righe ~334-480 dell'originale,
// prima dello split di questo file da collaboratorAccessForms.tsx): stesso markup, stessa
// logica, stessi service. Cambiano solo import/export (Task 2 del piano "Tab Info del
// drawer partner in stile Notion"). Non ridisegnato in questo task.

// Create Access Form
export function CreateAccessForm({
  partnerId,
  partnerName,
  onClose,
  onCreated
}: {
  partnerId: string;
  partnerName: string;
  onClose: () => void;
  onCreated: (password: string) => void;
}) {
  const { projects: allProjects, fetchProjects } = useProjectStore();
  const [creating, setCreating] = useState(false);
  const [generatedPassword] = useState(generatePassword);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [language, setLanguage] = useState('it');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const username = partnerName.toLowerCase().replace(/[^a-z0-9]/g, '.').replace(/\.+/g, '.').replace(/^\.|\.$/, '');

  const activeProjects = (allProjects || []).filter(p => p.status !== 'completed' && p.status !== 'cancelled' && !p.archived);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await CollaboratorAuthService.createCollaboratorAccess({
        external_partner_id: partnerId,
        username,
        password: generatedPassword,
        preferred_language: language,
        project_ids: selectedProjectIds
      });

      if (result) {
        onCreated(generatedPassword);
      } else {
        setError('Errore nella creazione. Username già in uso?');
      }
    } catch (err) {
      setError('Errore nella creazione dell\'accesso');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-black dark:text-white">Crea accesso portale progetti</h4>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-white/10 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Credentials */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">Username</label>
          <div className="px-3 py-2 bg-gray-100 dark:bg-white/10 rounded text-sm font-mono">{username}</div>
        </div>
        <div>
          <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">Password</label>
          <div className="px-3 py-2 bg-gray-100 dark:bg-white/10 rounded text-sm font-mono">{generatedPassword}</div>
        </div>
      </div>

      {/* Language */}
      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">
          Lingua portale
        </label>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
        >
          <option value="it">Italiano</option>
          <option value="en">English</option>
        </select>
      </div>

      {/* Project Selection */}
      <div>
        <label className="block text-xs font-medium text-black/60 dark:text-white/60 mb-1">
          Progetti accessibili ({selectedProjectIds.length} selezionati)
        </label>
        <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
          {activeProjects.length === 0 ? (
            <p className="p-3 text-sm text-gray-500 text-center">Nessun progetto attivo</p>
          ) : (
            activeProjects.map((project) => (
              <label
                key={project.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedProjectIds.includes(project.id)}
                  onChange={(e) => {
                    setSelectedProjectIds(prev =>
                      e.target.checked
                        ? [...prev, project.id]
                        : prev.filter(id => id !== project.id)
                    );
                  }}
                  className="rounded border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-black dark:text-white truncate block">{project.name}</span>
                  {project.client?.business_name && (
                    <span className="text-xs text-gray-500 truncate block">{project.client.business_name}</span>
                  )}
                </div>
              </label>
            ))
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Annulla
        </button>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
        >
          {creating ? <MinimalLoader size="sm" /> : <Plus className="w-4 h-4" />}
          Crea accesso
        </button>
      </div>
    </div>
  );
}
